import { useInfiniteQuery } from '@tanstack/react-query';

import { stringToDateRange } from '@/helpers';
import { FilterValues } from '@/hooks/use-filter-values';
import {
  DEFAULT_PAGE_SIZE_PROJECTS_CHANGES,
  flattenInfinitePages,
  getNextPageParam,
} from '@/lib/constants/pagination';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getProjectChanges } from '../api/projects.api';
import { mapProjectChangeTypeToDTO } from '../mappers/project-changes.mapper';
import { ProjectChangeType } from '../types/project-changes';

/**
 * Maps filter values to API request format for project changes
 */
const mapProjectChangesFilters = (filters: FilterValues['values'], projectId: number) => {
  const apiFilters: any = {
    projectId,
  };

  if (filters.changeType) {
    const changeTypes = Array.isArray(filters.changeType)
      ? filters.changeType
      : [filters.changeType];
    apiFilters.changedSection = changeTypes.map(type =>
      mapProjectChangeTypeToDTO(type as ProjectChangeType)
    );
  }

  if (filters.dateRange) {
    const dateRange = stringToDateRange(filters.dateRange as string);
    if (dateRange?.from) {
      apiFilters.fromDate = normalizeToStartOfDayUTC(dateRange.from).toISOString();
    }
    if (dateRange?.to) {
      apiFilters.toDate = normalizeToStartOfDayUTC(dateRange.to).toISOString();
    }
  }

  if (filters.changedBy) {
    apiFilters.changedByUserId = Number(filters.changedBy);
  }

  return apiFilters;
};

interface UseProjectChangesParams {
  projectId: number;
  filters?: FilterValues['values'];
  enabled?: boolean;
}

/**
 * Hook for fetching project changes with pagination and filtering
 */
export const useProjectChanges = ({
  projectId,
  filters = {},
  enabled = true,
}: UseProjectChangesParams) => {
  return useInfiniteQuery({
    queryKey: ['project-changes', projectId, filters],
    queryFn: ({ pageParam }) => {
      const requestFilters = mapProjectChangesFilters(filters, projectId);
      return getProjectChanges({
        ...requestFilters,
        pagination: {
          pageSize: DEFAULT_PAGE_SIZE_PROJECTS_CHANGES,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - changes don't update frequently during a session
    select: flattenInfinitePages,
  });
};
